import { useEffect, useState, useRef, useCallback } from "react";
import useThemeStore from "../../store/themeStore";
import { ThemeProps } from "../../utils/theme";
import { sendEnquiry } from "../../utils/properties";
import useTokenStore from "../../store/store";
import jwt2json from "../../utils/jwt2json";
import CalendarModal from "./CalendarModal";
import FormModal from "./FormModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  user?: {
    name: string;
    phoneNumber: string;
    email: string;
  };
}

const EMPTY = "";

// Service configuration mapping
const SERVICE_CONFIG: Record<string, {
  ownServiceKey: keyof ThemeProps;
  calendarLinkKey: keyof ThemeProps;
  contactEmailKey: keyof ThemeProps;
  modalFormKey: keyof ThemeProps;
  serviceName: string;
  formComponent?: string;
}> = {
  "661f66e260e8287c128c29fe": { // lettings
    ownServiceKey: "lettingOwnService",
    calendarLinkKey: "lettingCalendarLink", 
    contactEmailKey: "lettingContactEmail",
    modalFormKey: "lettingModalForm",
    serviceName: "Lettings"
  },
  "661f66e360e8287c128c2a20": { // listings
    ownServiceKey: "listingsOwnService",
    calendarLinkKey: "listingsCalendarLink",
    contactEmailKey: "listingsContactEmail",
    modalFormKey: "listingsModalForm",
    serviceName: "Listings"
  },
  "661f66e460e8287c128c2a28": { // digital plus
    ownServiceKey: "digitalPlusOwnService",
    calendarLinkKey: "digitalPlusCalendarLink",
    contactEmailKey: "digitalPlusContactEmail",
    modalFormKey: "digitalPlusModalForm",
    serviceName: "Digital Plus"
  },
  "661f66e460e8287c128c2a2e": { // refurbishment
    ownServiceKey: "refurbishmentOwnService",
    calendarLinkKey: "refurbishmentCalendarLink",
    contactEmailKey: "refurbishmentContactEmail", 
    modalFormKey: "refurbishmentModalForm",
    serviceName: "Refurbishment"
  },
  "661f66e460e8287c128c2a2a": { // brokerage
    ownServiceKey: "brokerageOwnService",
    calendarLinkKey: "brokerageCalendarLink",
    contactEmailKey: "brokerageContactEmail",
    modalFormKey: "brokerageModalForm",
    serviceName: "Brokerage"
  },
  "661f66e460e8287c128c2a2c": { // legals
    ownServiceKey: "legalsOwnService",
    calendarLinkKey: "legalsCalendarLink",
    contactEmailKey: "legalsContactEmail",
    modalFormKey: "legalsModalForm",
    serviceName: "Legals"
  },
  "6620cdcc3cadbd221fed7e94": { // deal sourcing
    ownServiceKey: "dealSourcingOwnService",
    calendarLinkKey: "dealSourcingCalendarLink",
    contactEmailKey: "dealSourcingContactEmail",
    modalFormKey: "dealSourcingModalForm",
    serviceName: "Deal Sourcing"
  },
  "6810fb5f78f60f116a169457": { // company setup
    ownServiceKey: "companySetupOwnService",
    calendarLinkKey: "companySetupCalendarLink",
    contactEmailKey: "companySetupContactEmail",
    modalFormKey: "companySetupModalForm",
    serviceName: "Company Setup",
    formComponent: "CompanySetupForm"
  },
  "6811f16578f60f116a169460": { // furnishing
    ownServiceKey: "furnishingOwnService",
    calendarLinkKey: "furnishingCalendarLink",
    contactEmailKey: "furnishingContactEmail",
    modalFormKey: "furnishingModalForm",
    serviceName: "Furnishing"
  },
  "6811f18778f60f116a169461": { // removals
    ownServiceKey: "removalsOwnService",
    calendarLinkKey: "removalsCalendarLink",
    contactEmailKey: "removalsContactEmail",
    modalFormKey: "removalsModalForm",
    serviceName: "Removals",
    formComponent: "RemovalsForm"
  },
  "6811f1a778f60f116a169462": { // surveying
    ownServiceKey: "surveyingOwnService",
    calendarLinkKey: "surveyingCalendarLink",
    contactEmailKey: "surveyingContactEmail",
    modalFormKey: "surveyingModalForm",
    serviceName: "Surveying",
    formComponent: "SurveyingForm"
  },
  "6811f1c878f60f116a169463": { // housing association
    ownServiceKey: "housingAssociationOwnService",
    calendarLinkKey: "housingAssociationCalendarLink",
    contactEmailKey: "housingAssociationContactEmail",
    modalFormKey: "housingAssociationModalForm",
    serviceName: "Housing Association"
  },
  "6811f1f578f60f116a169464": { // home insurance
    ownServiceKey: "homeInsuranceOwnService",
    calendarLinkKey: "homeInsuranceCalendarLink",
    contactEmailKey: "homeInsuranceContactEmail",
    modalFormKey: "homeInsuranceModalForm",
    serviceName: "Home Insurance"
  },

  // NEW: Additional Products - All using AdditionalProductForm
  "68930d5ae6efc18bc791b671": { // inspection
    ownServiceKey: "inspectionOwnService",
    calendarLinkKey: "inspectionCalendarLink",
    contactEmailKey: "inspectionContactEmail",
    modalFormKey: "inspectionModalForm",
    serviceName: "Inspection",
    formComponent: "AdditionalProductForm"
  },
  "68930d6be6efc18bc791b672": { // lra
    ownServiceKey: "lraOwnService",
    calendarLinkKey: "lraCalendarLink",
    contactEmailKey: "lraContactEmail",
    modalFormKey: "lraModalForm",
    serviceName: "LRA",
    formComponent: "AdditionalProductForm"
  },
  "68930d7de6efc18bc791b673": { // eicr inside m25
    ownServiceKey: "eicrInsideM25OwnService",
    calendarLinkKey: "eicrInsideM25CalendarLink",
    contactEmailKey: "eicrInsideM25ContactEmail",
    modalFormKey: "eicrInsideM25ModalForm",
    serviceName: "EICR Inside M25",
    formComponent: "AdditionalProductForm"
  },
  "68930d93e6efc18bc791b674": { // eicr outside m25
    ownServiceKey: "eicrOutsideM25OwnService",
    calendarLinkKey: "eicrOutsideM25CalendarLink",
    contactEmailKey: "eicrOutsideM25ContactEmail",
    modalFormKey: "eicrOutsideM25ModalForm",
    serviceName: "EICR Outside M25",
    formComponent: "AdditionalProductForm"
  },
  "68930dace6efc18bc791b675": { // gas cp12
    ownServiceKey: "gasCP12OwnService",
    calendarLinkKey: "gasCP12CalendarLink",
    contactEmailKey: "gasCP12ContactEmail",
    modalFormKey: "gasCP12ModalForm",
    serviceName: "Gas CP12",
    formComponent: "AdditionalProductForm"
  },
  "68930dc1e6efc18bc791b676": { // gas cp12 & boiler service
    ownServiceKey: "gasCP12BoilerServiceOwnService",
    calendarLinkKey: "gasCP12BoilerServiceCalendarLink",
    contactEmailKey: "gasCP12BoilerServiceContactEmail",
    modalFormKey: "gasCP12BoilerServiceModalForm",
    serviceName: "Gas CP12 & Boiler Service",
    formComponent: "AdditionalProductForm"
  },
  "68930ddbe6efc18bc791b677": { // boiler service
    ownServiceKey: "boilerServiceOwnService",
    calendarLinkKey: "boilerServiceCalendarLink",
    contactEmailKey: "boilerServiceContactEmail",
    modalFormKey: "boilerServiceModalForm",
    serviceName: "Boiler Service",
    formComponent: "AdditionalProductForm"
  },
  "68930df9e6efc18bc791b678": { // floorplan
    ownServiceKey: "floorplanOwnService",
    calendarLinkKey: "floorplanCalendarLink",
    contactEmailKey: "floorplanContactEmail",
    modalFormKey: "floorplanModalForm",
    serviceName: "Floorplan",
    formComponent: "AdditionalProductForm"
  },
  "689312b1e6efc18bc791b67c": { // epc vesa
    ownServiceKey: "epcVesaOwnService",
    calendarLinkKey: "epcVesaCalendarLink",
    contactEmailKey: "epcVesaContactEmail",
    modalFormKey: "epcVesaModalForm",
    serviceName: "EPC / VESA",
    formComponent: "AdditionalProductForm"
  },
  "689312c5e6efc18bc791b67d": { // inventory
    ownServiceKey: "inventoryOwnService",
    calendarLinkKey: "inventoryCalendarLink",
    contactEmailKey: "inventoryContactEmail",
    modalFormKey: "inventoryModalForm",
    serviceName: "Inventory",
    formComponent: "AdditionalProductForm"
  },
  "689312d7e6efc18bc791b67e": { // checkout
    ownServiceKey: "checkoutOwnService",
    calendarLinkKey: "checkoutCalendarLink",
    contactEmailKey: "checkoutContactEmail",
    modalFormKey: "checkoutModalForm",
    serviceName: "Checkout",
    formComponent: "AdditionalProductForm"
  },
  
  // Bundle Services - Also using AdditionalProductForm
  "68931707e6efc18bc791b679": { // bundle epc vesa floorplan
    ownServiceKey: "bundleEpcVesaFloorplanOwnService",
    calendarLinkKey: "bundleEpcVesaFloorplanCalendarLink",
    contactEmailKey: "bundleEpcVesaFloorplanContactEmail",
    modalFormKey: "bundleEpcVesaFloorplanModalForm",
    serviceName: "EPC / VESA / Floorplans Bundle",
    formComponent: "AdditionalProductForm"
  },
  "68931722e6efc18bc791b67a": { // bundle inventory & check in
    ownServiceKey: "bundleInventoryCheckInOwnService",
    calendarLinkKey: "bundleInventoryCheckInCalendarLink",
    contactEmailKey: "bundleInventoryCheckInContactEmail",
    modalFormKey: "bundleInventoryCheckInModalForm",
    serviceName: "Inventory & Check In Bundle",
    formComponent: "AdditionalProductForm"
  },
  "6893173ae6efc18bc791b67b": { // bundle epc vesa lra
    ownServiceKey: "bundleEpcVesaLraOwnService",
    calendarLinkKey: "bundleEpcVesaLraCalendarLink",
    contactEmailKey: "bundleEpcVesaLraContactEmail",
    modalFormKey: "bundleEpcVesaLraModalForm",
    serviceName: "EPC / VESA & LRA Bundle",
    formComponent: "AdditionalProductForm"
  }
};


export default function FeatureEnquiryModal(props: Props) {
  const { isOpen, onClose, productId } = props;
  const jwt = useTokenStore((state) => state.token);
  const user = jwt ? jwt2json(jwt) : null;
  const userId: string | undefined =
    user && user._id != null && typeof user._id === "string" ? (user._id as string) : undefined;
  const [busy, busySet] = useState<boolean>(false);
  const [name, nameSet] = useState<string>(String(user?.name ?? EMPTY));
  const [phoneNumber, phoneNumberSet] = useState<string>(String(user?.phoneNumber ?? EMPTY));
  const [email, emailSet] = useState<string>(String(user?.email ?? EMPTY));
  const themes = useThemeStore((state) => state.theme as unknown as ThemeProps);

  const [statusMessage, setStatusMessage] = useState<string>("");
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  const [showFormModal, setShowFormModal] = useState<boolean>(false);

  // Form state management for Stripe integration
  const [formData, setFormData] = useState<any>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle');

  const messageTimer = useRef<NodeJS.Timeout | null>(null);
  const redirectTimer = useRef<NodeJS.Timeout | null>(null);
  const nastyFlag = useRef<boolean>(false);

  // Service configuration with safety checks
  const serviceConfig = SERVICE_CONFIG[productId];
  const isCompanySetup = productId === "6810fb5f78f60f116a169457";

  // SIMPLIFIED logic - get values directly from themes with fallbacks
  const getServiceValue = (key: keyof ThemeProps, fallback: any = false) => {
    if (!themes || !serviceConfig) return fallback;
    return themes[key] ?? fallback;
  };

  // Get service configuration values
  const isOwnService = getServiceValue(serviceConfig?.ownServiceKey, false);
  const hasModalForm = getServiceValue(serviceConfig?.modalFormKey, false);
  const calendarLinkValue = getServiceValue(serviceConfig?.calendarLinkKey, "");
  const hasCalendarLink = calendarLinkValue && String(calendarLinkValue).trim() !== '';

  // Proper logic with additional products override
  const shouldShowForm = hasModalForm || !!serviceConfig?.formComponent; // Force form for products with formComponent
  const shouldShowCalendar = hasCalendarLink && !shouldShowForm;

  // Debug logging
  console.log('🔍 Modal Logic Debug:', {
    productId,
    serviceName: serviceConfig?.serviceName,
    isCompanySetup,
    // Raw theme values
    themeKeys: themes ? Object.keys(themes).sort() : 'no themes',
    companySetupModalForm: themes?.companySetupModalForm,
    companySetupOwnService: themes?.companySetupOwnService,
    companySetupCalendarLink: themes?.companySetupCalendarLink,
    // Computed values
    isOwnService,
    hasModalForm,
    hasCalendarLink,
    shouldShowForm,
    shouldShowCalendar,
    // Service config
    serviceConfigExists: !!serviceConfig,
    modalFormKey: serviceConfig?.modalFormKey,
    calendarLinkKey: serviceConfig?.calendarLinkKey,
    // Hardcoded override for testing
    properDataFlow: 'Check if API /whitelabel/theme returns companySetup* properties'
  });

  const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement> | null = null) => {
    event?.preventDefault();
    busySet(true);
  
    const data = {
      name,
      phoneNumber,
      email,
      productId,
      userId,
      websiteName: themes?.name,
      websiteURL: themes?.website,
      serviceOwnService: !!isOwnService,
      calendarLinkUsed: shouldShowCalendar ? calendarLinkValue : null,
      calendarCompleted: false,
      formModalUsed: shouldShowForm,
      formCompleted: false,
    };
  
    // PRIORITY 1: Form Modal (if enabled) - HIGHEST PRIORITY
    if (shouldShowForm) {
      console.log('📋 Opening form modal for:', serviceConfig?.serviceName);
      setShowFormModal(true);
      
      // Process DB save in background
      sendEnquiry(data).catch(error => {
        console.error("Background enquiry save failed:", error);
      });
      
      busySet(false);
      return;
    }
  
    // PRIORITY 2: Calendar Modal (if link exists and no form)
    if (shouldShowCalendar) {
      console.log('📅 Opening calendar modal for:', serviceConfig?.serviceName);
      setShowCalendarModal(true);
      
      // Process DB save in background  
      sendEnquiry(data).catch(error => {
        console.error("Background enquiry save failed:", error);
      });
      
      busySet(false);
      return;
    }
  
    // PRIORITY 3: Default email flow
    try {
      console.log('📧 Using default email flow for:', serviceConfig?.serviceName);
      const result = await sendEnquiry(data);
      
      if (result?.status === 200 || result?.message?.toUpperCase() === "OK") {
        console.log('✅ Enquiry saved successfully');
  
        setStatusMessage("Thank you for your interest, the team will be in touch shortly...");
        setShowStatusModal(true);
        
        redirectTimer.current = setTimeout(() => {
          console.log('🕐 Status modal timeout triggered, closing modal...');
          
          // Clean up state properly
          setShowStatusModal(false);
          busySet(false);
          nastyFlag.current = false;
          
          // Small delay to ensure state updates
          setTimeout(() => {
            if (user === null) {
              console.log('👤 No user, redirecting to register...');
              onClose(); // Close the modal first
              window.location.href = "/register";
            } else {
              console.log('👤 User logged in, closing modal...');
              onClose();
            }
          }, 50);
        }, 3000);
      } else {
        throw new Error("Failed to save enquiry");
      }
    } catch (error) {
      console.error("Error when submitting form", error);
      alert("An error occurred while submitting your enquiry. Please try again later.");
      setShowStatusModal(false);
      busySet(false);
      nastyFlag.current = false;
    }
  }, [name, phoneNumber, email, productId, userId, themes, isOwnService, shouldShowCalendar, calendarLinkValue, shouldShowForm, serviceConfig, user, onClose]);

  const handleFormComplete = async (submittedFormData: any, requiresPayment: boolean = false) => {
    console.log('📋 Form completed:', { submittedFormData, requiresPayment });
    
    setFormData(submittedFormData);

    if (requiresPayment) {
      // Redirect to Stripe payment
      setPaymentStatus('processing');
      
      try {
        console.log('💳 Processing payment for:', submittedFormData);
        
        // Simulate payment processing
        setTimeout(() => {
          handlePaymentComplete({
            paymentId: 'pi_' + Date.now(),
            amount: 15000, // £150.00 in pence
            status: 'succeeded'
          });
        }, 2000);
        
      } catch (error) {
        console.error('Payment processing failed:', error);
        handlePaymentFailed(error, submittedFormData);
      }
    } else {
      // No payment required, complete the process
      await completeFormSubmission(submittedFormData);
    }
  };

  const handlePaymentComplete = async (paymentData: any) => {
    console.log('💳 Payment completed:', paymentData);
    setPaymentStatus('success');
    
    // Complete the form submission with payment info
    await completeFormSubmission({
      ...formData,
      paymentData
    });
  };

  const handlePaymentFailed = (error: any, failedFormData: any) => {
    console.error('💳 Payment failed:', error);
    setPaymentStatus('failed');
    
    // Keep form data so user can retry
    setFormData(failedFormData);
    
    // Show error message but keep form open
    alert('Payment failed. Please check your details and try again.');
  };

  const completeFormSubmission = async (finalFormData: any) => {
    try {
      // Update the existing database record to mark form as completed
      const updateData = {
        name,
        phoneNumber,
        email,
        productId,
        userId,
        websiteName: themes?.name,
        websiteURL: themes?.website,
        serviceOwnService: !!isOwnService,
        formModalUsed: true,
        formCompleted: true,
        formData: JSON.stringify(finalFormData),
        updateExisting: true, // Flag to indicate this is an update
      };

      const result = await sendEnquiry(updateData);
      
      if (result?.status === 200 || result?.message?.toUpperCase() === "OK") {
        console.log('✅ Form completion updated in database');
        
        setStatusMessage("Thank you! Your request has been submitted successfully. Our team will be in touch shortly.");
        setShowStatusModal(true);
        
        redirectTimer.current = setTimeout(() => {
          handleFormClose();
        }, 3000);
      } else {
        console.error('Failed to update form status:', result);
        handleFormClose();
      }
    } catch (error) {
      console.error("Error updating form completion status:", error);
      handleFormClose();
    }
  };

  const handleCalendarBookingComplete = async (appointmentData?: any) => {
    console.log('🎉 Calendar booking completed:', appointmentData);
    
    try {
      const updateData = {
        name,
        phoneNumber,
        email,
        productId,
        userId,
        websiteName: themes?.name,
        websiteURL: themes?.website,
        serviceOwnService: !!isOwnService,
        calendarLinkUsed: calendarLinkValue,
        calendarCompleted: true,
        appointmentDateTime: appointmentData?.appointmentDateTime,
        bookingDetectionData: JSON.stringify(appointmentData?.bookingData || {}),
        updateExisting: true,
      };

      const result = await sendEnquiry(updateData);
      
      if (result?.status === 200 || result?.message?.toUpperCase() === "OK") {
        console.log('✅ Calendar booking completion updated in database');
      } else {
        console.error('Failed to update booking status:', result);
      }
    } catch (error) {
      console.error("Error updating calendar completion status:", error);
    }
  };

  const handleCalendarClose = () => {
    setShowCalendarModal(false);
    nastyFlag.current = false;
    if (user === null) {
      onClose();
      setTimeout(() => {
        window.location.href = "/register";
      }, 100);
    } else {
      onClose();
    }
  };

  const handleFormClose = () => {
    setShowFormModal(false);
    setFormData(null);
    setPaymentStatus('idle');
    setShowStatusModal(false);
    nastyFlag.current = false;
    if (user === null) {
      onClose();
      setTimeout(() => {
        window.location.href = "/register";
      }, 100);
    } else {
      onClose();
    }
  };

  // Add a cleanup function for when the component unmounts or modal closes
  useEffect(() => {
    if (!isOpen) {
      // Clean up all timers and state when modal closes
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
        redirectTimer.current = null;
      }
      if (messageTimer.current) {
        clearTimeout(messageTimer.current);
        messageTimer.current = null;
      }
      setShowStatusModal(false);
      setShowCalendarModal(false);
      setShowFormModal(false);
      nastyFlag.current = false;
      busySet(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && user?.email && !busy && !nastyFlag.current) {
      nastyFlag.current = true;
      handleSubmit();
    }

    return () => {
      if (messageTimer.current) {
        clearTimeout(messageTimer.current);
        messageTimer.current = null;
      }
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
        redirectTimer.current = null;
      }
    };
  }, [isOpen, busy, handleSubmit, user?.email]);

  const updateField = (event: any, setFn: any) => setFn(event.target.value || "");

  const inputStyle = {
    padding: "10px",
    borderRadius: "5px",
    border: "1px solid black",
    boxSizing: "border-box" as "border-box",
  };

  const handleStatusModalClose = () => {
    // Clear any pending timers
    if (redirectTimer.current) {
      clearTimeout(redirectTimer.current);
      redirectTimer.current = null;
    }
    
    setShowStatusModal(false);
    busySet(false);
    nastyFlag.current = false;
    
    // Handle redirect/close based on user status
    if (user === null) {
      onClose();
      setTimeout(() => {
        window.location.href = "/register";
      }, 100);
    } else {
      onClose();
    }
  };

  const renderStatusModal = () => (
    <div
      className="modal-backdrop"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1001, // Higher than main modal
      }}
      onClick={handleStatusModalClose} // Allow clicking backdrop to close
    >
      <div
        className="modal-content"
        style={{
          position: "relative",
          background: "white",
          padding: "20px",
          borderRadius: "5px",
          width: "400px",
          maxWidth: "90%",
          textAlign: "center",
        }}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking modal content
      >
        <span
          onClick={handleStatusModalClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            cursor: "pointer",
            fontSize: "20px",
            fontWeight: "bold",
            transition: "transform 0.2s",
            display: "inline-block",
            color: "#666",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.3)";
            e.currentTarget.style.color = "#000";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.color = "#666";
          }}
        >
          &times;
        </span>
        <p style={{ marginTop: "10px", marginBottom: "0" }}>{statusMessage}</p>
      </div>
    </div>
  );

  const isWhiteOrShade = (color: string) => {
    if (color?.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;

    const rgba = color?.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }

    return false;
  };

  return (
    <>
      {isOpen && !user && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="modal-content"
            style={{
              position: "relative",
              background: "white",
              padding: "20px",
              borderRadius: "5px",
              width: "400px",
              maxWidth: "90%",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <span
              onClick={onClose}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                cursor: "pointer",
                fontSize: "20px",
                fontWeight: "bold",
                transition: "transform 0.2s",
                display: "inline-block",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.3)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              &times;
            </span>
            <p style={{ marginBottom: "10px" }}>
              Please leave your details and someone will be in touch
              {shouldShowCalendar && !shouldShowForm && (
                <span className="block text-sm text-blue-600 mt-1">
                  You'll then be able to book an appointment directly!
                </span>
              )}
              {shouldShowForm && (
                <span className="block text-sm text-green-600 mt-1">
                  You'll then be able to complete your service request!
                </span>
              )}
              {isOwnService && shouldShowCalendar && (
                <span className="block text-sm text-purple-600 mt-1">
                  Our team will handle this directly and you can book a consultation!
                </span>
              )}
            </p>
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <input
                style={inputStyle}
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => updateField(e, nameSet)}
                required
              />
              <input
                style={inputStyle}
                type="text"
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={(e) => updateField(e, phoneNumberSet)}
                required
              />
              <input
                style={inputStyle}
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => updateField(e, emailSet)}
                required
              />
              <button
                type="submit"
                disabled={busy}
                style={{
                  backgroundColor: themes?.color1 && !isWhiteOrShade(themes.color1) 
                    ? themes.color1 
                    : "#007BFF",
                  color: "white",
                  padding: "12px",
                  border: "none",
                  borderRadius: "5px",
                  cursor: busy ? "not-allowed" : "pointer",
                  opacity: busy ? 0.6 : 1,
                  fontWeight: "bold",
                }}
              >
                {busy ? "Processing..." : "Enquire Now"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Status Modal - Shows when showStatusModal is true */}
      {showStatusModal && renderStatusModal()}

      {/* Calendar Modal */}
      {showCalendarModal && serviceConfig && (
        <CalendarModal
          isOpen={showCalendarModal}
          onClose={handleCalendarClose}
          calendarLink={calendarLinkValue}
          onBookingComplete={handleCalendarBookingComplete}
          serviceName={serviceConfig.serviceName}
        />
      )}

      {/* Form Modal */}
      {showFormModal && serviceConfig?.formComponent && (
        <FormModal
          isOpen={showFormModal}
          onClose={handleFormClose}
          formType={serviceConfig.formComponent}
          serviceName={serviceConfig.serviceName}
          onFormComplete={handleFormComplete}
          onPaymentComplete={handlePaymentComplete}
          onPaymentFailed={handlePaymentFailed}
          themes={themes}
          initialFormData={formData}
          paymentStatus={paymentStatus}
        />
      )}
    </>
  );
}